import i18n, { type PostProcessorModule } from "i18next"
import { initReactI18next } from "react-i18next"
import ca from "../../../messages/ca.json"
import es from "../../../messages/es.json"
import en from "../../../messages/en.json"

/**
 * Resolves next-intl style ICU plurals — `{count, plural, =0 {…} =1 {…} other {# …}}` —
 * which plain i18next renders verbatim. Runs before interpolation replaces `{count}`,
 * so the raw pattern is still intact here. Exact `=N` matches win over the
 * Intl.PluralRules category, and `#` expands to the count.
 */
const ICU_PLURAL = /\{\s*(\w+)\s*,\s*plural\s*,\s*((?:[^{}]|\{[^{}]*\})*)\}/g

function selectPluralForm(body: string, count: number, locale: string): string | null {
	const cases: Record<string, string> = {}
	const caseRe = /(=\d+|zero|one|two|few|many|other)\s*\{((?:[^{}]|\{[^{}]*\})*)\}/g
	for (let m = caseRe.exec(body); m; m = caseRe.exec(body)) cases[m[1]] = m[2]
	const category = new Intl.PluralRules(locale).select(count)
	const form = cases[`=${count}`] ?? cases[category] ?? cases.other
	return form === undefined ? null : form.replace(/#/g, String(count))
}

const icuPluralPostProcessor: PostProcessorModule = {
	type: "postProcessor",
	name: "icuPlural",
	process(value, _key, options, translator) {
		if (typeof value !== "string" || !value.includes("plural,")) return value
		const locale = (translator as { language?: string })?.language || "ca"
		return value.replace(ICU_PLURAL, (match, varName: string, body: string) => {
			const raw = (options as Record<string, unknown>)?.[varName]
			const count = typeof raw === "number" ? raw : Number(raw)
			if (!Number.isFinite(count)) return match
			return selectPluralForm(body, count, locale) ?? match
		})
	},
}

// Read persisted locale from localStorage (Zustand persist key)
function getPersistedLocale(): string {
	try {
		const raw = localStorage.getItem("locale-store")
		if (raw) {
			const parsed = JSON.parse(raw)
			const locale = parsed?.state?.locale
			if (locale && ["ca", "es", "en"].includes(locale)) return locale
		}
	} catch {}
	return "ca"
}

i18n.use(icuPluralPostProcessor).use(initReactI18next).init({
	resources: {
		ca: { translation: ca },
		es: { translation: es },
		en: { translation: en },
	},
	lng: getPersistedLocale(),
	fallbackLng: "ca",
	postProcess: ["icuPlural"],
	interpolation: {
		// Use single braces like next-intl: {variable} instead of {{variable}}
		prefix: "{",
		suffix: "}",
		escapeValue: false,
	},
	// Flatten nested keys: t("namespace.key") works with nested JSON
	keySeparator: ".",
	nsSeparator: false,
})

export default i18n
